import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  alpha,
} from "@mui/material";
import { TopLevelPaper } from "../../../../../../../themes/StyledComponents";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DateRangeIcon from "@mui/icons-material/DateRange";
import ReceiptIcon from "@mui/icons-material/Receipt";
import numeral from "numeral";
import dayjs from "dayjs";
import {
  TrendAnalyticsResponseDto,
  MonthlyTransactionTrendDto,
  WeeklyTransactionTrendDto,
  MonthlyRxTrendDto,
  WeeklyRxTrendDto,
} from "../../../../../../../api-client";

interface Props {
  data: TrendAnalyticsResponseDto;
}

type ViewMode = "transactions" | "rxTypes";
type PeriodMode = "monthly" | "weekly";

const fmtEuro = (n: number) => numeral(n).format("0,0.00 $");
const fmtNumber = (n: number) => numeral(n).format("0,0");

const TrendsPanel: React.FC<Props> = ({ data }) => {
  const [viewMode, setViewMode] = useState<ViewMode>("transactions");
  const [periodMode, setPeriodMode] = useState<PeriodMode>("monthly");

  // Format data for tables
  const transactionChartData = useMemo(() => {
    const trends =
      periodMode === "monthly"
        ? data.monthlyTransactionTrends
        : data.weeklyTransactionTrends;

    return trends.map((trend) => {
      const isMonthly = "month" in trend;
      const periodDisplay = isMonthly
        ? dayjs(trend.month).format("MMM YYYY")
        : `${dayjs((trend as WeeklyTransactionTrendDto).weekStart).format(
            "DD MMM"
          )} - ${dayjs((trend as WeeklyTransactionTrendDto).weekEnd).format(
            "DD MMM"
          )}`;
      const fullPeriod = isMonthly
        ? trend.month
        : (trend as WeeklyTransactionTrendDto).week;

      return {
        period: periodDisplay,
        fullPeriod: fullPeriod,
        transactions: trend.totalTransactions,
        amount: trend.totalAmount,
        rxs: trend.totalRxs,
        payers: trend.uniquePayersCount,
        completionRate: trend.completionRate,
      };
    });
  }, [data, periodMode]);

  const rxTypesChartData = useMemo(() => {
    const trends =
      periodMode === "monthly" ? data.monthlyRxTrends : data.weeklyRxTrends;

    // Get all unique rx types across all periods
    const allRxTypes = new Set<string>();
    trends.forEach((trend) => {
      Object.keys(trend.rxTypes as Record<string, number>).forEach((type) =>
        allRxTypes.add(type)
      );
    });

    return trends.map((trend) => {
      const isMonthly = "month" in trend;
      const periodDisplay = isMonthly
        ? dayjs(trend.month).format("MMM YYYY")
        : `${dayjs((trend as WeeklyRxTrendDto).weekStart).format(
            "DD MMM"
          )} - ${dayjs((trend as WeeklyRxTrendDto).weekEnd).format("DD MMM")}`;
      const fullPeriod = isMonthly
        ? trend.month
        : (trend as WeeklyRxTrendDto).week;

      const baseData = {
        period: periodDisplay,
        fullPeriod: fullPeriod,
        totalRxs: trend.totalRxs,
      };

      // Add each rx type as a separate property
      const rxTypeData: Record<string, number> = {};
      const rxTypesObj = trend.rxTypes as Record<string, number>;
      allRxTypes.forEach((type) => {
        rxTypeData[type] = rxTypesObj[type] || 0;
      });

      return { ...baseData, ...rxTypeData };
    });
  }, [data, periodMode]);

  const renderTransactionTrends = () => (
    <Grid container spacing={2}>
      {/* Transaction Data Table */}
      <Grid item xs={12}>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 1,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-track": {
              bgcolor: alpha("#F9F3EF", 0.6),
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "#456882",
              borderRadius: 3,
            },
          }}
        >
          <Table
            sx={{
              "& .MuiTableCell-stickyHeader": {
                bgcolor: "#456882",
                color: "#fff",
                backgroundClip: "padding-box",
                zIndex: 1,
              },
            }}
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  Periode
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  Rechnungen
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  Rezepte
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  Gesamt (€)
                </TableCell>
                <TableCell
                  align="right"
                  sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  Kostenträger
                </TableCell>
                {/* <TableCell
                  align="right"
                  sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}
                >
                  Abschlussrate (%)
                </TableCell> */}
              </TableRow>
            </TableHead>
            <TableBody>
              {transactionChartData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:nth-of-type(odd)": {
                      bgcolor: alpha("#F9F3EF", 0.3),
                    },
                    "&:hover": {
                      bgcolor: alpha("#456882", 0.1),
                    },
                  }}
                >
                  <TableCell sx={{ fontWeight: 500, fontSize: "0.9rem" }}>
                    {row.period}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: "0.9rem" }}>
                    {fmtNumber(row.transactions)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: "0.9rem" }}>
                    {fmtNumber(row.rxs)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: "0.9rem" }}>
                    {fmtEuro(row.amount)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: "0.9rem" }}>
                    {fmtNumber(row.payers)}
                  </TableCell>
                  {/* <TableCell align="right" sx={{ fontSize: "0.9rem" }}>
                    {row.completionRate.toFixed(1)}%
                  </TableCell> */}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );

  const renderRxTypeTrends = () => {
    const allRxTypes = [
      ...new Set(
        rxTypesChartData.flatMap((item) =>
          Object.keys(item).filter(
            (key) => !["period", "fullPeriod", "totalRxs"].includes(key)
          )
        )
      ),
    ];

    return (
      <Grid container spacing={2}>
        {/* Rx Types Data Table */}
        <Grid item xs={12}>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 1,
              overflowX: "auto",
              overflowY: "auto",
              "&::-webkit-scrollbar": { width: 6, height: 6 },
              "&::-webkit-scrollbar-track": {
                bgcolor: alpha("#F9F3EF", 0.6),
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#456882",
                borderRadius: 3,
              },
            }}
          >
            <Table
              sx={{
                "& .MuiTableCell-stickyHeader": {
                  bgcolor: "#456882",
                  color: "#fff",
                  backgroundClip: "padding-box",
                  zIndex: 1,
                },
                minWidth: 650,
              }}
              stickyHeader
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}
                  >
                    Periode
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: "#fff", fontWeight: 600, fontSize: "0.95rem" }}
                  >
                    Gesamt Rx
                  </TableCell>
                  {allRxTypes.map((key) => (
                    <TableCell
                      key={key}
                      align="right"
                      sx={{
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: "0.95rem",
                      }}
                    >
                      {key}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rxTypesChartData.map((row, index) => (
                  <TableRow
                    key={index}
                    sx={{
                      "&:nth-of-type(odd)": {
                        bgcolor: alpha("#F9F3EF", 0.3),
                      },
                      "&:hover": {
                        bgcolor: alpha("#456882", 0.1),
                      },
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500, fontSize: "0.9rem" }}>
                      {row.period}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: "0.9rem" }}>
                      {fmtNumber(row.totalRxs)}
                    </TableCell>
                    {allRxTypes.map((key) => (
                      <TableCell
                        key={key}
                        align="right"
                        sx={{ fontSize: "0.9rem" }}
                      >
                        {fmtNumber((row as any)[key] || 0)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    );
  };

  return (
    <TopLevelPaper
      elevation={1}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "0px",
      }}
    >
      {/* Header with controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={450}
          color="text.primary"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <TrendingUpIcon />
          Trends & Entwicklungen
        </Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {/* View Mode Toggle */}
          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={{
              "& .MuiToggleButton-root": {
                borderRadius: 1,
                color: "#456882",
                "&.Mui-selected": {
                  bgcolor: "#456882",
                  color: "#fff",
                  "&:hover": { bgcolor: "#456882" },
                },
              },
            }}
          >
            <ToggleButton value="transactions">
              <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5 }} />
              Transaktionen
            </ToggleButton>
            <ToggleButton value="rxTypes">
              <ReceiptIcon fontSize="small" sx={{ mr: 0.5 }} />
              Rezept-Typen
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Period Mode Toggle */}
          <ToggleButtonGroup
            size="small"
            value={periodMode}
            exclusive
            onChange={(_, v) => v && setPeriodMode(v)}
            sx={{
              "& .MuiToggleButton-root": {
                borderRadius: 1,
                color: "#456882",
                "&.Mui-selected": {
                  bgcolor: "#456882",
                  color: "#fff",
                  "&:hover": { bgcolor: "#456882" },
                },
              },
            }}
          >
            <ToggleButton value="monthly">
              <DateRangeIcon fontSize="small" sx={{ mr: 0.5 }} />
              Monatlich
            </ToggleButton>
            <ToggleButton value="weekly">
              <CalendarTodayIcon fontSize="small" sx={{ mr: 0.5 }} />
              Wöchentlich
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Chart Content */}
      {viewMode === "transactions"
        ? renderTransactionTrends()
        : renderRxTypeTrends()}
    </TopLevelPaper>
  );
};

export default TrendsPanel;
